import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Upload, Search, Plus, Trash2, Pencil, FolderPlus } from "lucide-react";

const EMPTY_FORM = {
  email: "",
  first_name: "",
  last_name: "",
  company: "",
  list_ids: [],
  unsubscribed: false,
};

function ListsPicker({ value, onChange, lists, testidPrefix }) {
  if (!lists.length) {
    return (
      <div className="text-xs text-muted-foreground border border-dashed border-border p-3 rounded-sm">
        Nessuna lista disponibile. Creane una dalla sezione Liste.
      </div>
    );
  }
  return (
    <div className="border border-border rounded-sm p-3 max-h-40 overflow-y-auto space-y-2">
      {lists.map((l) => (
        <label
          key={l.id}
          className="flex items-center gap-2 text-sm cursor-pointer hover:text-primary"
        >
          <Checkbox
            checked={value.includes(l.id)}
            onCheckedChange={() =>
              onChange(
                value.includes(l.id)
                  ? value.filter((x) => x !== l.id)
                  : [...value, l.id]
              )
            }
            data-testid={`${testidPrefix}-list-${l.id}`}
          />
          <span className="flex-1">{l.name}</span>
          <span className="text-xs text-muted-foreground">{l.contact_count}</span>
        </label>
      ))}
    </div>
  );
}

export default function ContactsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialList = searchParams.get("list") || "__all__";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [listFilter, setListFilter] = useState(initialList);
  const [lists, setLists] = useState([]);
  const [selected, setSelected] = useState(new Set());

  const [openNew, setOpenNew] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [openBulk, setOpenBulk] = useState(false);
  const [bulkLists, setBulkLists] = useState([]);
  const [openBulkDelete, setOpenBulkDelete] = useState(false);
  const [deleteAllMatching, setDeleteAllMatching] = useState(false);

  const loadLists = () => api.get("/lists").then(({ data }) => setLists(data));

  const load = async () => {
    const params = { limit: 500 };
    if (q) params.q = q;
    if (listFilter && listFilter !== "__all__") params.list_id = listFilter;
    const { data } = await api.get("/contacts", { params });
    setItems(data.items);
    setTotal(data.total);
    setSelected(new Set());
  };

  useEffect(() => {
    loadLists();
  }, []);

  useEffect(() => {
    load();
    if (listFilter && listFilter !== "__all__") {
      setSearchParams({ list: listFilter });
    } else if (searchParams.get("list")) {
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, listFilter]);

  const filteredListName = useMemo(() => {
    if (listFilter === "__all__") return null;
    return lists.find((l) => l.id === listFilter)?.name;
  }, [listFilter, lists]);

  const openCreate = () => {
    setForm({
      ...EMPTY_FORM,
      list_ids: listFilter !== "__all__" ? [listFilter] : [],
    });
    setOpenNew(true);
  };

  const create = async () => {
    if (!form.email) return toast.error("Email obbligatoria");
    try {
      await api.post("/contacts", form);
      toast.success("Contatto aggiunto");
      setOpenNew(false);
      setForm(EMPTY_FORM);
      load();
      loadLists();
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    }
  };

  const openEditDialog = (c) => {
    setEditingId(c.id);
    setForm({
      email: c.email,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      company: c.company || "",
      list_ids: c.list_ids || [],
      unsubscribed: !!c.unsubscribed,
    });
    setOpenEdit(true);
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/contacts/${editingId}`, {
        first_name: form.first_name,
        last_name: form.last_name,
        company: form.company,
        list_ids: form.list_ids,
        unsubscribed: form.unsubscribed,
      });
      toast.success("Contatto aggiornato");
      setOpenEdit(false);
      setEditingId(null);
      load();
      loadLists();
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo contatto?")) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Contatto eliminato");
    load();
    loadLists();
  };

  const toggleSel = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };
  const toggleSelAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((c) => c.id)));
  };

  const applyBulk = async () => {
    if (!bulkLists.length) return toast.error("Seleziona almeno una lista");
    if (!selected.size) return toast.error("Seleziona almeno un contatto");
    try {
      // Merge lists on each selected contact
      const selectedItems = items.filter((c) => selected.has(c.id));
      for (const c of selectedItems) {
        const merged = Array.from(new Set([...(c.list_ids || []), ...bulkLists]));
        await api.patch(`/contacts/${c.id}`, { list_ids: merged });
      }
      toast.success(`${selected.size} contatti aggiornati`);
      setOpenBulk(false);
      setBulkLists([]);
      load();
      loadLists();
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    }
  };

  const runBulkDelete = async () => {
    try {
      let payload;
      if (deleteAllMatching) {
        payload = {
          all_matching: true,
          list_id: listFilter !== "__all__" ? listFilter : null,
        };
      } else {
        if (!selected.size) return toast.error("Seleziona almeno un contatto");
        payload = { ids: Array.from(selected) };
      }
      const { data } = await api.post("/contacts/bulk-delete", payload);
      toast.success(
        data.deleted === 1
          ? "1 contatto eliminato"
          : `${data.deleted} contatti eliminati`
      );
      setOpenBulkDelete(false);
      setDeleteAllMatching(false);
      setSelected(new Set());
      load();
      loadLists();
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    }
  };

  return (
    <>
      <PageHeader
        title="Contatti"
        subtitle={
          filteredListName
            ? `${total} contatti nella lista "${filteredListName}"`
            : `${total} contatti totali`
        }
        testid="contacts-header"
        actions={
          <>
            <Button
              variant="outline"
              onClick={openCreate}
              data-testid="new-contact-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Nuovo
            </Button>
            <Button
              onClick={() => navigate("/contatti/importa")}
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Importa CSV
            </Button>
          </>
        }
      />
      <PageBody>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cerca per email, nome, azienda…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="contacts-search"
            />
          </div>
          <Select value={listFilter} onValueChange={setListFilter}>
            <SelectTrigger className="w-56" data-testid="contacts-list-filter">
              <SelectValue placeholder="Tutte le liste" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte le liste</SelectItem>
              {lists.map((l) => (
                <SelectItem key={l.id} value={l.id}>
                  {l.name} ({l.contact_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selected.size > 0 && (
            <div
              className="flex items-center gap-2 ml-auto"
              data-testid="bulk-actions"
            >
              <span className="text-sm text-muted-foreground">
                {selected.size} selezionati
              </span>
              <Button
                variant="outline"
                onClick={() => setOpenBulk(true)}
                data-testid="bulk-assign-btn"
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                Aggiungi a lista
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setDeleteAllMatching(false);
                  setOpenBulkDelete(true);
                }}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina
              </Button>
            </div>
          )}
          {selected.size === 0 && total > 0 && (
            <div className="ml-auto">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => {
                  setDeleteAllMatching(true);
                  setOpenBulkDelete(true);
                }}
                data-testid="delete-all-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina tutti {listFilter !== "__all__" ? "nella lista" : `(${total})`}
              </Button>
            </div>
          )}
        </div>

        <div className="border border-border bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={items.length > 0 && selected.size === items.length}
                    onCheckedChange={toggleSelAll}
                    data-testid="select-all"
                  />
                </TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Azienda</TableHead>
                <TableHead>Liste</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-16">
                    <div className="text-muted-foreground mb-3">
                      Nessun contatto ancora.
                    </div>
                    <Button
                      onClick={() => navigate("/contatti/importa")}
                      data-testid="empty-import"
                    >
                      <Upload className="w-4 h-4 mr-2" /> Importa il tuo primo CSV
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(c.id)}
                      onCheckedChange={() => toggleSel(c.id)}
                      data-testid={`select-${c.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{c.email}</TableCell>
                  <TableCell>
                    {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.company || "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(c.list_ids || []).map((lid) => {
                        const l = lists.find((x) => x.id === lid);
                        return l ? (
                          <Badge key={lid} variant="secondary">
                            {l.name}
                          </Badge>
                        ) : null;
                      })}
                      {(!c.list_ids || c.list_ids.length === 0) && (
                        <span className="text-muted-foreground text-xs">
                          Nessuna
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {c.unsubscribed ? (
                      <Badge variant="destructive">Disiscritto</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Attivo</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(c)}
                        data-testid={`edit-contact-${c.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => del(c.id)}
                        data-testid={`delete-contact-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </PageBody>

      {/* New contact dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="new-contact-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo contatto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="new-contact-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input
                  value={form.first_name}
                  onChange={(e) =>
                    setForm({ ...form, first_name: e.target.value })
                  }
                  data-testid="new-contact-firstname"
                />
              </div>
              <div>
                <Label>Cognome</Label>
                <Input
                  value={form.last_name}
                  onChange={(e) =>
                    setForm({ ...form, last_name: e.target.value })
                  }
                  data-testid="new-contact-lastname"
                />
              </div>
            </div>
            <div>
              <Label>Azienda</Label>
              <Input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                data-testid="new-contact-company"
              />
            </div>
            <div>
              <Label>Liste</Label>
              <ListsPicker
                value={form.list_ids}
                onChange={(v) => setForm({ ...form, list_ids: v })}
                lists={lists}
                testidPrefix="new-contact"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>
              Annulla
            </Button>
            <Button onClick={create} data-testid="new-contact-save">
              Aggiungi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit contact dialog */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent data-testid="edit-contact-dialog">
          <DialogHeader>
            <DialogTitle>Modifica contatto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input value={form.email} disabled />
              <p className="text-xs text-muted-foreground mt-1">
                L'email non è modificabile. Elimina e ricrea il contatto se necessario.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input
                  value={form.first_name}
                  onChange={(e) =>
                    setForm({ ...form, first_name: e.target.value })
                  }
                  data-testid="edit-contact-firstname"
                />
              </div>
              <div>
                <Label>Cognome</Label>
                <Input
                  value={form.last_name}
                  onChange={(e) =>
                    setForm({ ...form, last_name: e.target.value })
                  }
                  data-testid="edit-contact-lastname"
                />
              </div>
            </div>
            <div>
              <Label>Azienda</Label>
              <Input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                data-testid="edit-contact-company"
              />
            </div>
            <div>
              <Label>Liste</Label>
              <ListsPicker
                value={form.list_ids}
                onChange={(v) => setForm({ ...form, list_ids: v })}
                lists={lists}
                testidPrefix="edit-contact"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={form.unsubscribed}
                onCheckedChange={(v) => setForm({ ...form, unsubscribed: !!v })}
                data-testid="edit-contact-unsub"
              />
              Disiscritto (non riceverà più campagne)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEdit(false)}>
              Annulla
            </Button>
            <Button onClick={saveEdit} data-testid="edit-contact-save">
              Salva modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk assign dialog */}
      <Dialog open={openBulk} onOpenChange={setOpenBulk}>
        <DialogContent data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle>Aggiungi {selected.size} contatti a una lista</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              I contatti selezionati verranno aggiunti alle liste scelte (le
              appartenenze esistenti vengono mantenute).
            </p>
            <ListsPicker
              value={bulkLists}
              onChange={setBulkLists}
              lists={lists}
              testidPrefix="bulk"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenBulk(false)}>
              Annulla
            </Button>
            <Button onClick={applyBulk} data-testid="bulk-assign-save">
              Aggiungi a lista
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog open={openBulkDelete} onOpenChange={setOpenBulkDelete}>
        <DialogContent data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>
              {deleteAllMatching
                ? listFilter !== "__all__"
                  ? `Eliminare tutti i ${total} contatti nella lista "${filteredListName}"?`
                  : `Eliminare TUTTI i ${total} contatti?`
                : `Eliminare ${selected.size} contatti selezionati?`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Questa operazione è <strong>irreversibile</strong>. I contatti verranno rimossi definitivamente dal database.
            </p>
            {deleteAllMatching && listFilter === "__all__" && (
              <div className="border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                Stai per eliminare l'intera rubrica contatti. Assicurati di avere una copia del CSV.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenBulkDelete(false)} data-testid="bulk-delete-cancel">
              Annulla
            </Button>
            <Button variant="destructive" onClick={runBulkDelete} data-testid="bulk-delete-confirm">
              <Trash2 className="w-4 h-4 mr-2" />
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
